import React, { useCallback, useMemo, useRef } from 'react';
import { View, Text, Animated } from 'react-native';
import { FlashList, type ListRenderItemInfo } from '@shopify/flash-list';
import { Circle, Tv, Trophy, Calendar, type LucideIcon } from 'lucide-react-native';
import { getGamesListStyles } from './GamesList.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { FadeInContent } from '../../shared/FadeInContent';
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn';
import type { GameResult } from '../../../src/gameResults/types/gameResults.types';
import {
  groupGameResultsByLeague,
  type LeagueGroup,
} from '../../../src/gameResults/helpers/groupGameResultsByLeague.helper';
import { getResolvedScoreboardIanaTimeZone } from '../../../src/helpers/scoreboardIntlTimeZone.helper';
import {
  SECTION_FILTER_KEYS,
  EMPTY_STATE_MESSAGES,
  EMPTY_STATE_SUBTITLES,
  SCOREBOARD_EMPTY_DATE,
  SCOREBOARD_EMPTY_LEAGUE,
  type SectionFilterKey,
} from '../../../src/gameResults/constants/gameResults.constants';
import { LeagueSection } from '../LeagueSection';
import { LeagueSectionByDate } from '../LeagueSectionByDate';
import { LeagueSectionSkeleton } from '../LeagueSectionSkeleton';

const EMPTY_STATE_ICON_COMPONENTS: Record<SectionFilterKey, LucideIcon> = {
  [SECTION_FILTER_KEYS.ALL]: Circle,
  [SECTION_FILTER_KEYS.LIVE]: Tv,
  [SECTION_FILTER_KEYS.RECENT]: Trophy,
  [SECTION_FILTER_KEYS.UPCOMING]: Calendar,
};

export interface GamesListProps {
  filteredResults: GameResult[];
  isLoading: boolean;
  /** When set, only show this section (all / live / recent / upcoming). */
  sectionFilter?: SectionFilterKey;
  /** Selected date YYYY-MM-DD; when Live or viewByLeague, helper ignores it. */
  selectedDate: string;
  /** When true, skip date filter so all dates for selected league are shown. */
  viewByLeague?: boolean;
  /** IANA zone from screen (must match scoreboard API ?timezone=). */
  dayBucketIanaTimeZone?: string;
  testID?: string;
}

export const GamesList: React.FC<GamesListProps> = ({
  filteredResults,
  isLoading,
  sectionFilter = SECTION_FILTER_KEYS.ALL,
  selectedDate,
  viewByLeague = false,
  dayBucketIanaTimeZone: dayBucketProp,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getGamesListStyles(theme), [theme]);

  const dayBucketIanaTimeZone = useMemo(
    () => dayBucketProp ?? getResolvedScoreboardIanaTimeZone(),
    [dayBucketProp]
  );

  const groupedLeagues = useMemo(
    () =>
      groupGameResultsByLeague(
        filteredResults,
        selectedDate,
        sectionFilter,
        viewByLeague,
        dayBucketIanaTimeZone
      ),
    [filteredResults, selectedDate, sectionFilter, viewByLeague, dayBucketIanaTimeZone]
  );

  const contentOpacity = useRef(new Animated.Value(1)).current;

  const keyExtractor = useCallback((item: LeagueGroup) => item.leagueName, []);

  const renderLeagueItem = useCallback(
    ({ item, index }: ListRenderItemInfo<LeagueGroup>) => (
      <StaggeredFadeIn index={index}>
        <LeagueSection
          leagueName={item.leagueName}
          games={item.games}
          testID={testID ? `${testID}-${item.leagueName}` : undefined}
        />
      </StaggeredFadeIn>
    ),
    [testID]
  );

  if (isLoading) {
    return <LeagueSectionSkeleton />;
  }

  const renderEmptyState = (filter: SectionFilterKey) => {
    const EmptyIcon = EMPTY_STATE_ICON_COMPONENTS[filter] ?? Circle;
    return (
    <View style={styles.emptyContainer} testID={testID ? `${testID}-empty` : 'games-list-empty'}>
      <View style={styles.emptyIconWrap}>
        <EmptyIcon
          size={44}
          color={theme.primary}
          style={styles.emptyIcon}
        />
      </View>
      <Text style={styles.emptyTitle}>{EMPTY_STATE_MESSAGES[filter]}</Text>
      <Text style={styles.emptySubtitle}>{EMPTY_STATE_SUBTITLES[filter]}</Text>
    </View>
  );
  };

  const renderEmptyDateState = () => (
    <View style={styles.emptyContainer} testID={testID ? `${testID}-empty-date` : 'games-list-empty-date'}>
      <View style={styles.emptyIconWrap}>
        <Calendar
          size={44}
          color={theme.primary}
          style={styles.emptyIcon}
        />
      </View>
      <Text style={styles.emptyTitle}>{SCOREBOARD_EMPTY_DATE.TITLE}</Text>
      <Text style={styles.emptySubtitle}>{SCOREBOARD_EMPTY_DATE.SUBTITLE}</Text>
    </View>
  );

  const renderEmptyLeagueState = () => (
    <View style={styles.emptyContainer} testID={testID ? `${testID}-empty-league` : 'games-list-empty-league'}>
      <View style={styles.emptyIconWrap}>
        <Circle
          size={44}
          color={theme.primary}
          style={styles.emptyIcon}
        />
      </View>
      <Text style={styles.emptyTitle}>{SCOREBOARD_EMPTY_LEAGUE.TITLE}</Text>
      <Text style={styles.emptySubtitle}>{SCOREBOARD_EMPTY_LEAGUE.SUBTITLE}</Text>
    </View>
  );

  if (groupedLeagues.length === 0) {
    const showLeagueEmpty = viewByLeague;
    const showDateEmpty =
      !viewByLeague && sectionFilter === SECTION_FILTER_KEYS.ALL;
    // flex:1 wrapper so the empty state fills and centers in the parent View
    // (the date path no longer relies on a ScrollView's flexGrow content style).
    const wrapperStyle = styles.leagueListWrapper;
    return (
      <FadeInContent
        opacity={contentOpacity}
        style={wrapperStyle}
        testID={testID || 'games-list'}
      >
        {showLeagueEmpty
          ? renderEmptyLeagueState()
          : showDateEmpty
            ? renderEmptyDateState()
            : renderEmptyState(sectionFilter)}
      </FadeInContent>
    );
  }

  if (viewByLeague) {
    const allGames = groupedLeagues.flatMap((g) => g.games);
    return (
      <FadeInContent
        opacity={contentOpacity}
        style={styles.leagueListWrapper}
        testID={testID || 'games-list'}
      >
        <LeagueSectionByDate
          games={allGames}
          testID={testID ? `${testID}-by-date` : undefined}
        />
      </FadeInContent>
    );
  }

  return (
    <FadeInContent
      opacity={contentOpacity}
      style={styles.dateListWrapper}
      testID={testID || 'games-list'}
    >
      <FlashList<LeagueGroup>
        data={groupedLeagues}
        keyExtractor={keyExtractor}
        renderItem={renderLeagueItem}
        contentContainerStyle={styles.dateListContent}
        showsVerticalScrollIndicator={false}
      />
    </FadeInContent>
  );
};
